import { ComponentFactory, ComponentFactoryResolver, Injectable, Type } from "@angular/core";

/**
 * Service to be able to register and get the component factory of a lazy-loaded components.
 */
@Injectable({
	providedIn: "root",
})
export class ComponentFactoryService {
	private componentResolverList = new Map<Type<any>, ComponentFactoryResolver>();

	constructor(private defaultResolver: ComponentFactoryResolver) {}

	/**
	 * Register the component factory resolver of lazy-loaded modules (typically in the constructor of the lazy-loaded modules).
	 * It enables creating lazy-loaded components dynamically (typically for dialogs/modals).
	 */
	registerLazyLoadedResolver(resolverFactory: ComponentFactoryResolver, ...layoutComponents: Type<any>[]) {
		for (const layout of layoutComponents) {
			this.componentResolverList.set(layout, resolverFactory);
		}
	}

	/**
	 * Returns the component factory resolver for a component. Supports getting the components of lazy-loaded modules.
	 */
	getResolver(component: Type<any>): ComponentFactoryResolver {
		return this.componentResolverList.get(component) || this.defaultResolver;
	}

	/**
	 * Returns the component factory for a component. Supports getting the components of lazy-loaded modules.
	 */
	get(component: Type<any>): ComponentFactory<any> {
		const resolver = this.getResolver(component);
		return resolver.resolveComponentFactory(component);
	}
}
